import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { SliderInput } from './SliderInput';
import { InvestmentTypeSelector } from './InvestmentTypeSelector';
import { FrequencySelector } from './FrequencySelector';
import { ContributionPause } from './ContributionPause';
import { TopUpWithdrawals } from './TopUpWithdrawals';
import { ChildFutureInputs } from './ChildFutureInputs';
import { CreditInputs } from './CreditInputs';
import { Calculator as CalcIcon } from 'lucide-react';
import { INVESTMENT_TYPES } from '../../utils/taxEngine';

export const CalculatorInputs = () => {
  const { inputs, updateInput, t, currency, investmentType } = useApp();

  const isAccumulation = investmentType === INVESTMENT_TYPES.ACCUMULATION_PLAN;
  const isChildFuture  = investmentType === INVESTMENT_TYPES.CHILD_FUTURE;
  const isSavings      = investmentType === INVESTMENT_TYPES.SAVINGS_ACCOUNT;
  const isCredit       = investmentType === INVESTMENT_TYPES.CREDIT;
  const showCompound   = isAccumulation || isChildFuture;

  const interestRateConfig = isSavings
    ? { min: 0.5, max: 5,  step: 0.1 }
    : { min: 0,   max: 25, step: 0.5 };

  const currencySuffix = currency === 'EUR' ? ' EUR' : ' RON';

  return (
    <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden">
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
            <CalcIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t('calculator')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-8">
        <InvestmentTypeSelector />

        {isCredit ? (
          <CreditInputs />
        ) : (
          <>
            <div className="space-y-6">
              <SliderInput
                label={t('initialDeposit')}
                value={inputs.initialDeposit ?? 0}
                onChange={(v) => updateInput('initialDeposit', v)}
                min={0} max={500000} step={1000}
                suffix={currencySuffix}
                tooltip={t('tooltipInitialDeposit')}
              />
              <SliderInput
                label={t('monthlyContribution')}
                value={inputs.monthlyContribution ?? 0}
                onChange={(v) => updateInput('monthlyContribution', v)}
                min={0} max={10000} step={50}
                suffix={currencySuffix}
                tooltip={t('tooltipMonthlyContribution')}
              />
              <SliderInput
                label={t('annualInterestRate')}
                value={inputs.annualInterestRate ?? 0}
                onChange={(v) => updateInput('annualInterestRate', v)}
                min={interestRateConfig.min}
                max={interestRateConfig.max}
                step={interestRateConfig.step}
                suffix="%"
                tooltip={t('tooltipInterestRate')}
              />
              <SliderInput
                label={t('investmentPeriod')}
                value={inputs.years ?? 20}
                onChange={(v) => updateInput('years', v)}
                min={1} max={50} step={1}
                suffix={` ${(inputs.years ?? 20) === 1 ? 'an' : 'ani'}`}
                tooltip={t('tooltipPeriod')}
              />
            </div>

            {isChildFuture && <ChildFutureInputs />}

            {showCompound && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FrequencySelector type="compounding" />
                <FrequencySelector type="contribution" />
              </div>
            )}

            <ContributionPause />

            {(isAccumulation || isChildFuture) && <TopUpWithdrawals />}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CalculatorInputs;
